import React, { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Button, Card, TextField } from '@ui'
import { listOrgs } from '../api.js'
import { MetricCard } from '../components/MetricCard.jsx'
import { AdminsPill } from '../components/AdminsPill.jsx'
import { CreateOrgModal } from './CreateOrgModal.jsx'

export function OrgsList({ showCreate = false }) {
  const nav = useNavigate()
  const [searchParams] = useSearchParams()
  const orphanedOnly = searchParams.get('filter') === 'orphaned'
  const [orgs, setOrgs] = useState(null)
  const [error, setError] = useState(null)
  const [filter, setFilter] = useState('')

  async function refresh() {
    try { setOrgs(await listOrgs()) }
    catch (e) { setError(e.message || 'Failed to load orgs') }
  }
  useEffect(() => { refresh() }, [])

  const filtered = useMemo(() => {
    if (!orgs) return null
    let list = orgs
    if (orphanedOnly) {
      list = list.filter((t) => (t.activeAdmins || 0) === 0 && (t.pendingAdmins || 0) === 0)
    }
    const q = filter.trim().toLowerCase()
    if (!q) return list
    return list.filter((t) =>
      t.name.toLowerCase().includes(q) || t.slug.toLowerCase().includes(q)
    )
  }, [orgs, filter, orphanedOnly])

  const totals = useMemo(() => {
    if (!orgs) return { orgs: 0, users: 0, pending: 0 }
    return {
      orgs: orgs.length,
      users: orgs.reduce((a, t) => a + (t.userCount || 0), 0),
      pending: orgs.reduce((a, t) => a + (t.pendingAdmins || 0), 0),
    }
  }, [orgs])

  return (
    <>
      <div style={pageStyles.header}>
        <div>
          <h2 style={pageStyles.title}>Orgs</h2>
          <p style={pageStyles.sub}>{totals.orgs} total orgs</p>
        </div>
        <Button onClick={() => nav('/orgs/new')}>+ New org</Button>
      </div>

      <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
        <MetricCard label="Total orgs" value={totals.orgs} />
        <MetricCard label="Total users" value={totals.users} />
        <MetricCard label="Pending invites" value={totals.pending} />
      </div>

      {orphanedOnly && (
        <div style={pageStyles.chip}>
          <span>Showing orphaned orgs (no admin confirmed)</span>
          <button
            onClick={() => nav('/orgs')}
            aria-label="Clear orphaned filter"
            style={pageStyles.chipClose}
          >
            ×
          </button>
        </div>
      )}

      <Card style={{ padding: 16 }}>
        <TextField
          placeholder="Search name or slug…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          style={{ maxWidth: 320, marginBottom: 16 }}
        />
        {error && <p style={{ color: 'var(--danger-text)' }}>{error}</p>}
        {filtered === null ? (
          <p>Loading…</p>
        ) : filtered.length === 0 ? (
          <p style={{ opacity: 0.7 }}>No orgs.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border)' }}>
                <th style={th}>Org</th>
                <th style={th}>Users</th>
                <th style={th}>Admins</th>
                <th style={th}>Created</th>
                <th style={th} />
              </tr>
            </thead>
            <tbody>
              {filtered.map((t) => (
                <tr key={t.orgId} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={td}>
                    <Link to={`/orgs/${t.orgId}`}>{t.name}</Link>
                    <div style={{ fontSize: 12, opacity: 0.6 }}>{t.slug}</div>
                  </td>
                  <td style={td}>{t.userCount ?? 0}</td>
                  <td style={td}>
                    <AdminsPill active={t.activeAdmins} pending={t.pendingAdmins} />
                  </td>
                  <td style={td}>{formatDate(t.createdAt)}</td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    <Button size="sm" variant="secondary" onClick={() => nav(`/orgs/${t.orgId}`)}>
                      View
                    </Button>{' '}
                    <Button size="sm" variant="secondary" onClick={() => nav(`/orgs/${t.orgId}/invite`)}>
                      Invite admin
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {showCreate && (
        <CreateOrgModal
          onClose={() => nav('/orgs')}
          onCreated={(orgId) => nav(`/orgs/${orgId}`)}
        />
      )}
    </>
  )
}

const th = { padding: '8px 12px', fontWeight: 600, fontSize: 12, opacity: 0.7 }
const td = { padding: '12px', verticalAlign: 'top' }
const pageStyles = {
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 20,
  },
  title: { fontSize: 20, fontWeight: 600, marginBottom: 2 },
  sub: { fontSize: 12, color: 'var(--text-secondary)' },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 10px',
    background: 'var(--border)',
    borderRadius: 16,
    fontSize: 12,
    marginBottom: 12,
  },
  chipClose: {
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: 16,
    lineHeight: 1,
    padding: 0,
  },
}

function formatDate(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}
